import {
    getPullSyncProviderJobStatus,
    createPullSyncProviderIntegration,
    getPullSyncProviderIntegrations,
    getProviderMetadataById,
    updatePullSyncProviderJoinField,
} from './metadata-providers.utils';

export const PROVIDER_AUTHENTICATION = {
    ERROR: -1,
    NOT_STARTED: 0,
    PENDING: 1,
    COMPLETE: 2,
};

const pullSyncDefaultState = {
    created: false,
    createError: null,
    lookupError: null,
    joinFieldError: null,
    metadataFetchError: null,
    kind: 'PluginMetadataSync',
    auth: {},
    name: '',
    lastSyncedAt: null,
    joinField: '',
    syncInfo: {},
    metadataList: {},
    jobStatus: {},
};

const metadataProviders = {
    namespaced: true,
    state: {},
    mutations: {
        setProviderCreated (state, { providerKey, value = true }) {
            if (!state[providerKey]) state[providerKey] = {};
            state[providerKey].created = value;
        },
        setProviderCreateError (state, { providerKey, error }) {
            if (!state[providerKey]) state[providerKey] = {};
            state[providerKey].createError = error;
        },
        setProviderLookupError (state, { providerKey, error }) {
            if (!state[providerKey]) state[providerKey] = {};
            state[providerKey].lookupError = error;
        },
        setProviderDefaultState (state, { providerKey }) {
            if (!state[providerKey]) state[providerKey] = {};
            Object.keys(pullSyncDefaultState).forEach((prop) => {
                state[providerKey][prop] = pullSyncDefaultState[prop];
            });
        },
        setProviderState (state, { providerKey, providerObj }) {
            state[providerKey] = Object.assign({}, pullSyncDefaultState, providerObj);
        },
        setJoinField (state, { providerKey, newJoinField }) {
            if (!state[providerKey]) state[providerKey] = {};
            state[providerKey].joinField = newJoinField;
        },
        setJoinFieldError (state, { providerKey, error }) {
            if (!state[providerKey]) state[providerKey] = {};
            state[providerKey].joinFieldError = error;
        },
        setProviderMetadataList (state, { providerKey, metadataList }) {
            if (!state[providerKey]) state[providerKey] = {};
            state[providerKey].metadataList = metadataList;
        },
        setJobStatus (state, { providerKey, jobStatus }) {
            if (!state[providerKey]) state[providerKey] = {};
            state[providerKey].jobStatus = jobStatus;
        },
        setProviderMetadataFetchError (state, { providerKey, error }) {
            if (!state[providerKey]) state[providerKey] = {};
            state[providerKey].metadataFetchError = error;
        },
    },
    actions: {
        async addProviderToSubscription ({ commit }, { name, level }) {
            const group = `pendo_${name}`;
            const providerKey = `${level}-${group}`;

            try {
                const { data } = await createPullSyncProviderIntegration({ name, metadataKind: level, group });
                commit('setProviderState', { providerKey, providerObj: data });
            } catch (error) {
                commit('setProviderCreateError', { name, error });
                commit('setProviderDefaultState', { providerKey });
            }

            commit('setProviderCreated', { providerKey, value: true });
        },
        async fetchProviderInformation ({ state, commit, dispatch }, { providerName, level }) {
            const providerKey = getProviderKey(providerName, level);

            try {
                const { data } = await getPullSyncProviderIntegrations();

                if (!data.length) {
                    commit('setProviderCreated', { providerKey, value: false });

                    return;
                }

                const provider = data.find(
                    (integration) => integration.name === providerName && integration.metadataKind === level,
                );

                if (!provider) {
                    commit('setProviderCreated', { providerKey, value: false });

                    return;
                }

                commit('setProviderState', { providerKey, providerObj: provider });
                commit('setProviderCreated', { providerKey, value: true });

                const { rootVersionId } = state[providerKey];
                await dispatch('updateProviderJobStatus', { providerName, level, rootVersionId });
            } catch (error) {
                commit('setProviderLookupError', { providerKey, error });
            }
        },
        async fetchProviderMetadataByKind ({ commit }, { providerName, level, providerId }) {
            const providerKey = getProviderKey(providerName, level);

            try {
                const { data } = await getProviderMetadataById(providerId, providerName);
                const metadataList = Object.keys(data).map((key) => {
                    return {
                        id: key,
                        ...data[key],
                    };
                });
                commit('setProviderMetadataList', { providerKey, metadataList });
            } catch (error) {
                commit('setProviderMetadataFetchError', { providerKey, error });
            }
        },
        async updateProviderJobStatus ({ state, commit }, { providerName, level, rootVersionId }) {
            const providerKey = getProviderKey(providerName, level);
            const { lastSyncedAt } = state[providerKey];
            let data;
            let status;

            try {
                const jobResponse = await getPullSyncProviderJobStatus(rootVersionId);
                if (!jobResponse) {
                    data = { lastUpdatedAt: lastSyncedAt };
                    status = 204;
                } else {
                    ({ data, status } = jobResponse);
                }

                const jobStatus = Object.assign({}, { ...data, statusCode: status });
                commit('setJobStatus', { providerKey, jobStatus });
            } catch {
                const jobStatus = {
                    lastUpdatedAt: lastSyncedAt,
                    status: 'error',
                };
                commit('setJobStatus', { providerKey, jobStatus });
            }
        },
        async updateProviderJoinField ({ state, commit }, { providerName, level, joinField, rootVersionId }) {
            const group = `pendo_${providerName}`;
            const providerKey = `${level}-${group}`;
            const { metadataList } = state[providerKey];

            try {
                const { data } = await updatePullSyncProviderJoinField(rootVersionId, joinField);
                commit('setProviderState', { providerKey, providerObj: data });
                commit('setProviderMetadataList', { providerKey, metadataList });
                commit('setProviderCreated', { providerKey, value: true });
            } catch (error) {
                commit('setJoinFieldError', { providerKey, error });
            }
        },
    },
};

function getProviderKey (providerName, level) {
    const group = `pendo_${providerName}`;

    return `${level}-${group}`;
}

export default metadataProviders;
